import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpLeft, Check, ShieldCheck } from "lucide-react";
import { signupHref } from "./content";
import "./pricing.css";
import "./price-stage.css";

const fmt = (n) => Number(n || 0).toLocaleString("en-US");

// The price is printed in the page's own language. The CMS stores an ISO
// code ("EGP"); anything else is shown as stored.
const CURRENCY_LABEL = { EGP: "ج.م" };
export const currencyLabel = (c) => CURRENCY_LABEL[c] || c || "ج.م";

// «كل حاجة مفتوحة» only when it is literally true of the plan on sale:
// every optional feature the platform has, and no employee cap. Read from
// the live overlay (Controller/HomeContent.js withPlans), so an admin edit
// on /admin/plans can never leave the badge saying something false.
export const opensEverything = (plan, catalog) => {
    const all = (catalog?.optional || []).map((f) => f.key);
    const has = new Set(plan?.featureKeys || []);
    return all.length > 0 && all.every((k) => has.has(k)) && plan?.employeesLimit == null;
};

// One plan on sale: the plan gets the whole stage, with the yearly price as
// a line under it rather than a toggle. Amber is spent on two things only —
// the price and the button.
const SoloPlan = ({ plan, currency, trialDays, catalog }) => {
    const monthly = Number(plan.monthly) || 0;
    const yearly = Number(plan.yearly) || 0;
    const saved = monthly > 0 && yearly > 0 ? Math.round((monthly * 12 - yearly) / monthly) : 0;
    const features = plan.features || [];
    const everything = opensEverything(plan, catalog);

    return (
        <article className="lp-price" data-reveal="scale">
            <div className="lp-price-main">
                {everything ? <span className="lp-price-badge">اشتراك واحد · كل حاجة مفتوحة</span> : null}
                <h3 className="lp-price-name">{plan.name}</h3>
                {plan.desc ? <p className="lp-price-desc">{plan.desc}</p> : null}

                <div className="lp-price-figure">
                    <strong className="num">{fmt(monthly)}</strong>
                    <span>{currency}<br />في الشهر</span>
                </div>
                {yearly > 0 ? (
                    <p className="lp-price-yearly">
                        أو <b className="num">{fmt(yearly)}</b> {currency} للسنة كلها
                        {saved > 0 ? <em> — وفّر {saved === 1 ? "شهر" : saved === 2 ? "شهرين" : `${saved} شهور`}</em> : null}
                    </p>
                ) : null}

                <Link to={signupHref(plan.ctaHref)} className="lp-btn lp-btn--primary lp-price-cta" data-primary-cta>
                    {plan.cta || "ابدأ شهرك المجاني"}
                    <ArrowUpLeft size={18} aria-hidden="true" />
                </Link>

                <ul className="lp-price-fine">
                    {trialDays ? (
                        <li><ShieldCheck size={16} aria-hidden="true" /> أول <span className="num">{trialDays}</span> يوم مجانًا، من غير كارت.</li>
                    ) : null}
                    <li>
                        <ShieldCheck size={16} aria-hidden="true" />
                        {" بعد التجربة الدفع بمحفظة إلكترونية أو إنستاباي — كل فترة دفعة واحدة، من غير تجديد تلقائي."}
                    </li>
                </ul>
            </div>

            {features.length ? (
                <div className="lp-price-side">
                    <span className="lp-price-side-title">كل ده جوّه الاشتراك</span>
                    <ul data-stagger>
                        {features.map((f, i) => (
                            <li key={`${plan.id}-${i}`} style={{ "--n": i + 1 }}>
                                <Check size={16} aria-hidden="true" />
                                <span>{f}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            ) : null}
        </article>
    );
};

const Pricing = ({ data = {} }) => {
    const [billing, setBilling] = useState("monthly");
    const plans = Array.isArray(data.plans) ? data.plans : [];
    // The yearly switch only when some plan is sold yearly; its tag from the
    // real prices (the plans come from the admin Plans page).
    const yearlyPlan = plans.find((p) => Number(p.monthly) > 0 && Number(p.yearly) > 0);
    const savedMonths = yearlyPlan ? Math.round((yearlyPlan.monthly * 12 - yearlyPlan.yearly) / yearlyPlan.monthly) : 0;
    const cycle = yearlyPlan ? billing : "monthly";
    const currency = currencyLabel(data.currency);

    if (!plans.length) return null;

    return (
        <section className="lp-band pricing" id="pricing" aria-labelledby="lp-pricing-title" data-scene>
            <div className="lp-container">
                <div className="lp-heading" data-reveal>
                    {data.eyebrow ? <span className="lp-eyebrow">{data.eyebrow}</span> : null}
                    {data.title ? <h2 className="lp-title" id="lp-pricing-title">{data.title}</h2> : null}
                    {data.subtitle ? <p className="lp-lede">{data.subtitle}</p> : null}
                </div>

                {plans.length === 1 ? (
                    <SoloPlan plan={plans[0]} currency={currency} trialDays={data.trialDays} catalog={data.featureCatalog} />
                ) : null}

                {plans.length > 1 && yearlyPlan ? <div className="pricing-toggle" role="tablist">
                    <button
                        type="button"
                        role="tab"
                        aria-selected={billing === "monthly"}
                        className={billing === "monthly" ? "active" : ""}
                        onClick={() => setBilling("monthly")}
                    >
                        شهري
                    </button>
                    <button
                        type="button"
                        role="tab"
                        aria-selected={billing === "yearly"}
                        className={billing === "yearly" ? "active" : ""}
                        onClick={() => setBilling("yearly")}
                    >
                        سنوي
                        {savedMonths > 0 ? (
                            <span className="pricing-toggle-tag">وفر {savedMonths === 1 ? "شهرًا" : savedMonths === 2 ? "شهرين" : `${savedMonths} أشهر`}</span>
                        ) : null}
                    </button>
                </div> : null}

                {plans.length > 1 ? <div className="pricing-grid" style={{ "--plan-cols": Math.min(Math.max(plans.length, 1), 3) }}>
                    {plans.map((plan, index) => {
                        const yearly = cycle === "yearly" && Number(plan.yearly) > 0;
                        const price = yearly ? plan.yearly : plan.monthly;
                        const perLabel = yearly ? "/ سنويًا" : "/ شهريًا";
                        const numericPrice = Number(price || 0);

                        return (
                            <article
                                className={`pricing-card ${plan.popular ? "popular" : ""}`}
                                key={plan.id || plan.name || index}
                                data-reveal
                                style={{ "--reveal-delay": `${index * 0.1}s` }}
                            >
                                {plan.popular && <span className="pricing-badge">الأكثر اختيارًا</span>}

                                <div className="pricing-card-head">
                                    <h3>{plan.name}</h3>
                                    {plan.desc ? <p>{plan.desc}</p> : null}
                                </div>

                                <div className="pricing-price">
                                    <strong className="num">{numericPrice.toLocaleString("en-US")}</strong>
                                    <span className="pricing-currency">{currency}</span>
                                    <span className="pricing-period">{perLabel}</span>
                                </div>

                                <ul className="pricing-feature-list">
                                    {(plan.features || []).map((feature, fi) => (
                                        <li key={`${plan.id || plan.name}-${fi}`}>
                                            <span className="pricing-check">
                                                <Check size={14} />
                                            </span>
                                            {feature}
                                        </li>
                                    ))}
                                </ul>

                                <Link to={signupHref(plan.ctaHref)} className="pricing-cta" data-primary-cta={plan.popular ? "" : undefined}>
                                    {plan.cta || "ابدأ الآن"}
                                </Link>
                            </article>
                        );
                    })}
                </div> : null}

                {data.footnote ? <p className="lp-price-footnote" data-reveal="fade">{data.footnote}</p> : null}
            </div>
        </section>
    );
};

export default Pricing;
